import { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout';
import { FaTimesCircle } from 'react-icons/fa';
import LinksGrid from '../../components/grids/LinksGrid';
import AddLinkForm from '../../components/FormComps/Links/AddLinkForm';
import EditLinkForm from '../../components/FormComps/Links/EditLinkForm';
import {
  getLinks,
  createLink,
  updateLink,
  deleteLink,
  Link,
} from '../../data/linkData';
import { canEditArea } from '../../helpers/areaAccess';

import '../Styles.css';

interface LinksProps {
  allowedRoutes?: string[];
}

const Links = (props: LinksProps) => {
  // Links.Manage or an administrator (see areaAccess.ts). The server
  // enforces the same.
  const canEdit = canEditArea(props.allowedRoutes, 'Links');
  const [linksData, setLinksData] = useState<Link[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [editFormVisible, setEditFormVisible] = useState(false);
  const [addFormVisible, setAddFormVisible] = useState(false);
  const [selectedLink, setSelectedLink] = useState<Link | null>(null);
  const { showToast } = useToast();

  const handleAddForm = () => {
    setAddFormVisible(true);
    setEditFormVisible(false);
    handleExpanded();
  };

  const handleEditForm = (link: Link) => {
    setSelectedLink(link);
    setEditFormVisible(true);
    setAddFormVisible(false);
    handleExpanded();
  };

  const handleExpanded = () => {
    setExpanded((prevState) => !prevState);

    if (editFormVisible) {
      setEditFormVisible(false);
    }
    if (addFormVisible) {
      setAddFormVisible(false);
    }
  };

  const handleSelect = () => {
    setExpanded(false);
  };

  const newLinkHandler = async (dataItem: any) => {
    try {
      await createLink(dataItem);

      const fetchData = async () => {
        const linksData = await getLinks();
        setLinksData(linksData);
      };
      fetchData();

      handleExpanded();
      showToast('Link successfully added', 'success');
    } catch (error: any) {
      handleExpanded();
      showToast('Failed to add link. Please try again.', 'error');
      console.error('Error adding link: ' + error.message);
    }
  };

  const updateLinkHandler = async (dataItem: any) => {
    try {
      await updateLink(dataItem.linkId, dataItem);

      const fetchData = async () => {
        const linksData = await getLinks();
        setLinksData(linksData);
      };
      fetchData();

      handleExpanded();
      showToast('Link successfully updated', 'success');
    } catch (error: any) {
      handleExpanded();
      showToast('Failed to update link. Please try again.', 'error');
      console.error('Error updating link: ' + error.message);
    }
  };

  const deleteLinkHandler = (id: number) => {
    deleteLink(id)
      .then(() => {
        setLinksData((prevData) =>
          prevData.filter((link) => link.linkId !== id)
        );
        handleExpanded();
        showToast('Link successfully deleted', 'success');
      })
      .catch((error) => {
        console.error('Error deleting link: ' + error.message);
        showToast('Error deleting link', 'error');
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const linksData = await getLinks();
        setLinksData(linksData);
      } catch (error: any) {
        console.error('Error fetching links: ' + error.message);
        showToast('Error loading links', 'error');
      }
    };

    fetchData();
  }, []);

  return (
    <div>
      <Drawer
        expanded={expanded}
        position={'end'}
        width={750}
        mode='overlay'
        animation={{
          duration: 400,
        }}
        onOverlayClick={handleExpanded}
        onSelect={handleSelect}
      >
        <DrawerContent>
          <DrawerNavigation>
            {addFormVisible && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Add Link</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <div className='form__wrapper'>
                  <AddLinkForm onSubmit={newLinkHandler} />
                </div>
              </>
            )}

            {editFormVisible && selectedLink && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>{canEdit ? 'Edit Link' : 'Link'}</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <div className='form__wrapper'>
                  <EditLinkForm
                    link={selectedLink}
                    onSubmit={updateLinkHandler}
                    onDelete={canEdit ? deleteLinkHandler : undefined}
                    readOnly={!canEdit}
                  />
                </div>
              </>
            )}
          </DrawerNavigation>

          <LinksGrid
            data={linksData}
            onAdd={canEdit ? handleAddForm : undefined}
            onEdit={handleEditForm}
          />
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default Links;
